import React, { useState } from 'react';
import { Lock, Pencil, Trash2 } from 'lucide-react';
import ProfileEditForm from './ProfileEditForm';
import DocManager from './DocManager';

const API = process.env.REACT_APP_BACKEND_URL;
const LOCKED_STATUSES = ['sent_to_college', 'approved', 'enrolled', 'rejected'];

/** Recruiter management block inside the student-detail dialog: edit details,
 *  manage documents (any stage) and delete — edit/delete lock once the
 *  application has been sent to a college. */
export default function RecruiterStudentManage({ student, onUpdated, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const locked = LOCKED_STATUSES.includes(student.status);

  const saveProfile = async (payload) => {
    const res = await fetch(`${API}/api/recruiter/students/${student.student_id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      return { ok: false, error: d.detail || 'Could not save changes.' };
    }
    onUpdated(await res.json());
    setEditing(false);
    return { ok: true };
  };

  const deleteStudent = async () => {
    if (!window.confirm(`Permanently delete ${student.student_name}'s profile? This cannot be undone.`)) return;
    setDeleting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/recruiter/students/${student.student_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Could not delete this student.');
        return;
      }
      onDeleted(student.student_id);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="border-t border-tge-navy/10 pt-4 space-y-4" data-testid="recruiter-student-manage">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-tge-navy text-xs uppercase tracking-wider">Manage this student</h4>
        {locked ? (
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-tge-navy/50 bg-tge-cream-soft px-2.5 py-1 rounded-full" data-testid="recruiter-manage-locked-badge" title="Editing and deletion lock once the application has been sent to a college. Documents can still be updated.">
            <Lock size={10} /> Application sent — details locked
          </span>
        ) : (
          <div className="flex items-center gap-2">
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold border border-tge-navy/20 text-tge-navy rounded-lg px-3 py-1.5 hover:bg-tge-cream-soft transition"
                data-testid="recruiter-edit-student-btn"
              >
                <Pencil size={11} /> Edit details
              </button>
            )}
            <button
              onClick={deleteStudent}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-rose-200 text-rose-600 rounded-lg px-3 py-1.5 hover:bg-rose-50 disabled:opacity-50 transition"
              data-testid="recruiter-delete-student-btn"
            >
              <Trash2 size={11} /> {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        )}
      </div>

      {editing && (
        <ProfileEditForm
          initial={student}
          onSave={saveProfile}
          onCancel={() => setEditing(false)}
          testIdPrefix="recruiter-edit"
        />
      )}

      <div>
        <div className="text-[11px] uppercase tracking-wider text-tge-muted font-semibold mb-1.5">
          Documents {locked && <span className="normal-case font-normal">(still editable after submission)</span>}
        </div>
        <DocManager
          documents={student.documents}
          canUpload
          buildViewUrl={(k) => `${API}/api/recruiter/students/${student.student_id}/documents/${k}`}
          buildUploadUrl={(k) => `${API}/api/recruiter/students/${student.student_id}/documents/${k}`}
          onChanged={async () => {
            const res = await fetch(`${API}/api/recruiter/students`, { credentials: 'include' });
            if (res.ok) {
              const fresh = ((await res.json()).students || []).find((s) => s.student_id === student.student_id);
              if (fresh) onUpdated(fresh);
            }
          }}
          testIdPrefix="recruiter-docs"
        />
      </div>
      {error && <div className="text-xs text-rose-600" data-testid="recruiter-manage-error">{error}</div>}
    </div>
  );
}
